/**
 * Request Detail edit-session helpers for #1154 repeatable instance lists.
 */

import type { RepeatableInstance } from './types';

function instanceSessionKey(instance: RepeatableInstance): string {
  const { id, createdAt: _ca, updatedAt: _ua, ...rest } = instance;
  return JSON.stringify({
    ...rest,
    id: id != null && id > 0 ? id : 'session',
  });
}

/** True when session list rows differ from the baseline captured at edit start. */
export function repeatableInstanceSessionHasChanges<
  T extends RepeatableInstance,
>(baseline: T[], session: T[]): boolean {
  if (baseline.length !== session.length) {
    return true;
  }
  return baseline.some(
    (item, index) =>
      instanceSessionKey(item) !== instanceSessionKey(session[index]),
  );
}

export function reconcileInstancesAfterConfigurationFlush<
  T extends RepeatableInstance,
  C extends { id?: number; displayOrder?: number },
>(sessionInstances: T[], flushedConfigurations: C[]): T[] {
  const byOrder = new Map(
    flushedConfigurations.map(config => [config.displayOrder ?? -1, config]),
  );
  return sessionInstances.map((instance, index) => {
    const order = instance.displayOrder ?? index + 1;
    const config = byOrder.get(order) ?? flushedConfigurations[index];
    if (config?.id != null && config.id > 0) {
      return { ...instance, id: config.id };
    }
    return instance;
  });
}

/** Keys stripped from request PATCH — draft configuration arrays flush separately. */
export const REPEATABLE_CONFIGURATION_EDITED_DATA_KEYS = [
  'smallUpsConfigurations',
  'coolingSystemConfigurations',
  'liquidCoolingCduConfigurations',
] as const;

/** Pre-#1333 UX instance arrays — must never PATCH onto `requests`. */
export const REPEATABLE_LEGACY_INSTANCE_EDITED_DATA_KEYS = [
  'smallUpsInstances',
  'coolingInstances',
  'liquidCoolingInstances',
] as const;

/** @deprecated Use REPEATABLE_CONFIGURATION_EDITED_DATA_KEYS */
export const REPEATABLE_INSTANCE_EDITED_DATA_KEYS =
  REPEATABLE_CONFIGURATION_EDITED_DATA_KEYS;

/** Client-cache repeatable rows — persist via offline cache / child APIs, not PATCH. */
export const REPEATABLE_CLIENT_CACHE_EDITED_DATA_KEYS = [
  'facilityPowerInstances',
  'rackConfigurations',
  'largeUpsConfigurations',
  'powerDistributionConfigurations',
] as const;

/** UX-only instance metadata that must never be PATCHed onto `requests`. */
export const REPEATABLE_INSTANCE_UX_ONLY_FLAT_KEYS = [
  'instanceName',
  'instanceStatus',
] as const;

const ALL_REPEATABLE_PATCH_STRIP_KEYS = [
  ...REPEATABLE_CONFIGURATION_EDITED_DATA_KEYS,
  ...REPEATABLE_LEGACY_INSTANCE_EDITED_DATA_KEYS,
  ...REPEATABLE_CLIENT_CACHE_EDITED_DATA_KEYS,
  ...REPEATABLE_INSTANCE_UX_ONLY_FLAT_KEYS,
] as const;

/** All keys stripped from Request Detail PATCH — used for parity checks with server. */
export const REPEATABLE_PATCH_STRIP_KEYS = ALL_REPEATABLE_PATCH_STRIP_KEYS;

/** True when every dirty field is client-only (no requests-table PATCH needed). */
export function dirtyFieldsAreOnlyRepeatablePatchKeys(
  dirtyFields: ReadonlySet<string> | readonly string[],
): boolean {
  const fields =
    dirtyFields instanceof Set ? [...dirtyFields] : [...dirtyFields];
  if (fields.length === 0) {
    return false;
  }
  const stripSet = new Set<string>(ALL_REPEATABLE_PATCH_STRIP_KEYS);
  return fields.every(field => stripSet.has(field));
}

export function stripRepeatableInstanceArraysFromPatch(
  data: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...data };
  for (const key of ALL_REPEATABLE_PATCH_STRIP_KEYS) {
    delete next[key];
  }
  return next;
}
